import { Platform } from 'react-native';
import moment from 'moment';
import { datadogLogs } from '@datadog/browser-logs';

import { env } from '@components/frame';

import { getDeviceDetails } from '@reducers/device';
import { isWeb, ENVIRONMENTS_NAME, getSecureValue, VERSION, navigationRef } from '@helpers';
import storeModule from '@store';

import type { RootState } from '@store/types';
import type { AppDispatch } from '@reducers/utils/types';
import type { DatadogConfig, LogEntry, LogPayload, GetStateFn, StatusType, SendLogParams } from './types';

// Re-export types for consumers
export type { LogEntry, LogPayload, StatusType, SendLogParams } from './types';

const DATADOG_CONFIG: DatadogConfig = {
  clientToken: 'pube50a2b716f3b237474311f7d92eab259',
  site: 'datadoghq.eu',
  apiUrl: 'https://http-intake.logs.datadoghq.eu/api/v2/logs',
  apiKey: '84cee4551fa8e03aa3483ce8e0561bd0',
};

const removePasswordKeys = (obj: Record<string, unknown> | unknown[] | unknown): typeof obj => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      obj[i] = removePasswordKeys(obj[i]);
    }
  } else {
    const record = obj as Record<string, unknown>;
    for (const key in record) {
      if (key === 'password' || key === 'Authorization') {
        delete record[key];
      } else {
        record[key] = removePasswordKeys(record[key]);
      }
    }
  }

  return obj;
};

// Initialize Datadog web SDK
const initDatadogWeb = (): void => {
  const config = datadogLogs.getInitConfiguration();
  if (!config) {
    datadogLogs.init({
      clientToken: DATADOG_CONFIG.clientToken,
      site: DATADOG_CONFIG.site,
      service: 'App.web',
      forwardErrorsToLogs: false,
      sampleRate: 100,
    });
  }
};

// Build common log payload from Redux state
const buildLogPayload = async (getState: GetStateFn): Promise<LogPayload> => {
  const {
    user: { userInfo },
    signalR: { appHubID, signalRAnonymID },
    notification: { ipAddress },
  } = getState();

  const tenantId = getSecureValue('tid');
  const user = { ...userInfo, userPicture: null };
  const device = await getDeviceDetails();
  const SignalR = {
    'AppHub-Connection-Id': appHubID,
    'OnboardingHub-Connection-Id': signalRAnonymID,
  };
  const platformName = `App.${Platform.OS}`;

  return { user, device, SignalR, tenantId, ipAddress, platformName };
};

export const sendToDatadog = (message: string, context: Record<string, unknown>, status: StatusType): void => {
  if (isWeb) {
    initDatadogWeb();
    datadogLogs.logger[status](message, context);
    return;
  }

  if (!process.env.HIDE_IN_WEB) {
    fetch(DATADOG_CONFIG.apiUrl, {
      // @ts-expect-error -- custom property used by the app's fetch interceptor
      ignoreInterceptor: 'true',
      method: 'POST',
      body: JSON.stringify([context]),
      headers: {
        'DD-API-KEY': DATADOG_CONFIG.apiKey,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  }
};

const getBrowserName = (): string => {
  if ((navigator as Record<string, unknown>).userAgentData) {
    const brands = ((navigator as Record<string, unknown>).userAgentData as Record<string, unknown>).brands as Array<{ brand: string }>;
    if (brands.some((b) => b.brand === 'Microsoft Edge')) return 'Edge';
    if (brands.some((b) => b.brand === 'Google Chrome')) return 'Chrome';
    if (brands.some((b) => b.brand === 'Chromium')) return 'Chromium';
  }
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome/.test(ua)) return 'Chrome';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Safari/.test(ua)) return 'Safari';
  return 'Unknown';
};

let memoryReporterInterval: ReturnType<typeof setInterval> | null = null;

export const startMemoryReporter = (): void => {
  if (!isWeb || !(performance as Record<string, unknown>).memory || memoryReporterInterval) return;

  initDatadogWeb();
  const browser = getBrowserName();

  memoryReporterInterval = setInterval(() => {
    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = (performance as Record<string, unknown>).memory as {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    const screen = navigationRef.current?.getCurrentRoute()?.name || 'Unknown';
    const state: RootState = storeModule.store.getState();
    const tenantId = getSecureValue('tid');
    const userInfo = state?.user?.userInfo;
    const ipAddress = state?.notification?.ipAddress;
    const user = userInfo ? { ...userInfo, userPicture: null } : null;

    datadogLogs.logger.info('memory_usage', {
      usedMB: (usedJSHeapSize / 1024 / 1024).toFixed(2),
      totalMB: (totalJSHeapSize / 1024 / 1024).toFixed(2),
      limitMB: (jsHeapSizeLimit / 1024 / 1024).toFixed(2),
      sessionDurationMin: (performance.now() / 1000 / 60).toFixed(1),
      screen,
      browser,
      user,
      Properties: {
        CompanyName: user?.companyProfile?.companyName,
        Ipaddress: ipAddress,
        TenantId: tenantId,
        UserEmailAddress: user?.emailAddress,
        UserName: `${user?.userProfile?.firstName} ${user?.userProfile?.lastName}`,
        Environment: ENVIRONMENTS_NAME[env],
        Url: window?.location?.href,
      },
      websiteURL: window?.location?.href,
    });
  }, 300000);
};

export const stopMemoryReporter = (): void => {
  if (memoryReporterInterval) {
    clearInterval(memoryReporterInterval);
    memoryReporterInterval = null;
  }
};

export const sendLog =
  ({ title, data = {}, status = 'info', id }: SendLogParams) =>
  async (_dispatch: AppDispatch, getState: GetStateFn): Promise<void> => {
    try {
      const { user, device, SignalR, tenantId, ipAddress, platformName } = await buildLogPayload(getState);

      let newData: Record<string, unknown> = data;
      try {
        newData = JSON.parse(JSON.stringify(newData));
        newData = removePasswordKeys(newData) as Record<string, unknown>;
      } catch (e) {
        console.log('Error when remove password');
      }

      const params: LogEntry[] = [
        {
          ddsource: platformName,
          ddtags: `env:${env},version:${VERSION}`,
          hostname: platformName,
          message: `${JSON.stringify(title)}, ${moment().format()}. IP: ${ipAddress}`,
          service: platformName,
          level: status,
          status: status,
          data: newData,
          user,
          device,
          Properties: {
            CompanyName: user?.companyProfile?.companyName,
            Ipaddress: ipAddress,
            MachineId: (data?.machine as Record<string, unknown>)?.machineId as string | undefined,
            MachineName: (data?.machine as Record<string, unknown>)?.shortName as string | undefined,
            TenantId: tenantId,
            UserEmailAddress: user?.emailAddress as string | undefined,
            UserName: `${(user?.userProfile as Record<string, unknown>)?.firstName} ${(user?.userProfile as Record<string, unknown>)?.lastName}`,
            Environment: ENVIRONMENTS_NAME[env],
            SignalR,
            Url: isWeb ? window.location.href : '',
          },
          websiteURL: window?.location?.href,
        },
      ];

      if (id) {
        params[0].request_id = id;
      }

      sendToDatadog(title, params[0], status);
    } catch (e) {
      console.log('Problem with logging', e);
    }
  };
